import { AUTH_TOKEN_SET, USER_ATTETION_VIEWED } from 'actions'
import { LayoutLoading } from 'components/LayoutLoading'
import { BorrowerAttentionModal } from 'components/Modals/BorrowerAttentionModal'
import { Overview } from 'components/Overview'
import jwtDecode from 'jwt-decode'
import { type IPipelineAttentionByLoan } from 'pages/Admin/AdminTools/Configuration/BorrowerPipelineAttentionTitles'
import { InvestorAuth } from 'pages/Investor/Auth'
import { BorrowerLabel } from 'pages/LoanApplication/config'
import { ParticipantAuth } from 'pages/Participants/Portal'
import { TemporaryAuth } from 'pages/Temporary'
import { useEffect, useMemo, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { LoanType } from 'reducers/loanDetail.reducer'
import { getBorrowerOutstandingItems, stopImpersonate } from 'services'
import { BorrowerHeader, Footer, HeaderSimple } from 'stories/layouts'
import { InvestorHeader } from 'stories/layouts/InvestorHeader'
import { ParticipantHeader } from 'stories/layouts/ParticipantHeader'
import { isPartyFilled } from 'utils'
import { applyImpersonationSession } from 'utils/applyImpersonationSession'
import { redirectAfterImpersonationIfNeeded } from 'utils/impersonationNavigation'
import { setBorrowerSeparator, setLoanNumber } from 'utils/setLoanNumber'

export const TemporaryLayout = (props: {
  children: any
  noOverview?: boolean
  title?: string
  requireSign?: boolean
  noHeader?: boolean
  backLink?: string
  noShadow?: boolean
}) => {
  const { children, noOverview, requireSign = false, noShadow = false } = props
  const urlParams: any = useParams()
  let { loanNumber, token } = urlParams
  const [isLoading, setLoading] = useState(true)
  const [isLoaded, setLoaded] = useState(false)
  const [isSigned, setSigned] = useState(false)
  const [noHeader, setNoHeader] = useState(false)
  const [noAuth, setNoAuth] = useState(false)
  const [_newToken, setNewToken] = useState('')
  const [loans, setLoans] = useState<Record<string, any>[]>([])
  const [conditions, setConditions] = useState<Record<number, string[]>>({})
  const [documents, setDocuments] = useState<Record<number, string[]>>({})
  const [signedStatus, setSignedStatus] = useState<Record<number, Record<string, any>>>({})
  const [assetStatus, setAssetStatus] = useState<Record<number, boolean>>({})
  const [borrowerRequiredParties, setBorrowerRequiredParties] = useState<Record<number, any>>({})
  const [pipelineAttention, setPipelineAttention] = useState<IPipelineAttentionByLoan>({})
  const [borrowerSep, setBorrowerSep] = useState()

  const location = useLocation()
  const navigate = useHistory()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { pathname } = useLocation()
  const isTemporaryUrl = window.location.href.length > 200

  const isBorrower = pathname.includes('/borrowerPipeline') || pathname.includes('borrowerlogin')
  // Every investor-portal screen. `/investorPortal` is the portal's own address; the two
  // `/participant...` forms are what it used to be called and still answer, so links in e-mails
  // sent before the rename keep landing on the right screen.
  const isParticipant =
    pathname.includes('/investorPortal') ||
    pathname.includes('/participantPipeline') ||
    pathname.includes('/participantPortal')
  const isLoanOptionsReview = pathname.includes('/loanOptionsReview')

  const isNewSignin = useMemo(() => {
    return [
      `/borrowerPipelines/signin`,
      `/investorPipelines/signin`,
      `/investorPortal/signin`,
      `/participantPipelines/signin`,
    ].includes(pathname)
  }, [])

  const hasBackButton = useMemo(() => {
    return pathname.includes('/borrowerPipeline') || urlParams.token == '_'
  }, [pathname, urlParams.token])

  const { registeredToken, loanType, attentionShowed, authProfile, impersonatedByName } = useSelector((state: any) => {
    return {
      registeredToken: state.auth.token,
      loanType: state.loanDetail.loanType,
      attentionShowed: state.auth.attentionShowed,
      authProfile: state.auth.profile,
      impersonatedByName: state.auth.role?.impersonatedByName,
    }
  })

  const handleStopImpersonate = async () => {
    try {
      const response = await stopImpersonate()
      await applyImpersonationSession(dispatch, queryClient, response)
      redirectAfterImpersonationIfNeeded(navigate, response.user)
    } catch {
      toast.error('Unable to return to your account')
    }
  }

  const impersonationBanner =
    authProfile?.impersonatedBy && (authProfile?.isBorrower || authProfile?.temparary) ? (
      <div className="bg-sky-50 text-sky-900 w-full px-3 py-2 text-center text-sm">
        Viewing as <span className="font-semibold">{authProfile.fullName || authProfile.name}</span>
        {' · '}
        <button type="button" className="font-semibold underline hover:text-sky-700" onClick={handleStopImpersonate}>
          Return to {impersonatedByName || 'your account'}
        </button>
      </div>
    ) : null

  if (token == '_' && registeredToken) token = undefined

  useEffect(() => {
    if (isNewSignin) {
      setLoaded(true)
      setLoading(false)
      return
    }

    if (token === undefined && registeredToken) {
      const payload: any = jwtDecode(registeredToken)
      if (payload.isSigned) {
        setSigned(true)
        setLoaded(true)
        setLoading(false)
        return
      }
    }

    setLoanNumber()

    if ((pathname.includes('investorPipeline') || isBorrower || isParticipant) && (!token || token.length < 20)) {
      setLoaded(true)
      setLoading(false)
      return
    }

    if (!token || (!loanNumber && loanNumber !== undefined)) {
      navigate.push('/')
      return
    }
    // A link mangled in transit decodes to nothing, and jwt-decode throws rather than returning
    // it. Thrown from here the whole page was lost -- this effect never reached `setLoaded`, so
    // every emailed signing and payment link rendered white with only a console error. A token
    // that will not decode is the same situation as one carrying the wrong loan, handled below:
    // send them to the front door.
    let payload: any
    try {
      payload = jwtDecode(token)
    } catch {
      navigate.push('/')
      return
    }
    if (payload.borrowerSeperator) {
      setBorrowerSeparator(payload.borrowerSeperator)
      setBorrowerSep(payload.borrowerSeperator)
    }
    if (!payload || (loanNumber !== undefined && payload.loanNumber != loanNumber)) {
      navigate.push('/')
      return
    }

    if (payload.noHeader) setNoHeader(true)
    if (payload.noAuth) setNoAuth(true)

    if (!requireSign || payload.noAuth) {
      dispatch({
        type: AUTH_TOKEN_SET,
        token,
        user: payload,
      })
      setSigned(true)
    } else {
      if (payload.isSigned) {
        // if (!registeredToken) {
        dispatch({
          type: AUTH_TOKEN_SET,
          token,
          user: payload,
        })
        // }
        setSigned(true)
      } else {
        setSigned(false)
      }
    }
    setLoaded(true)
    setLoading(false)
  }, [])

  const onSigned = async (newToken: string) => {
    const payload: any = jwtDecode(newToken)

    // if (!payload || (loanNumber !== undefined && payload.loanNumber != loanNumber)) {
    //   navigate.push('/')
    //   return
    // }

    dispatch({
      type: AUTH_TOKEN_SET,
      token: newToken,
      user: payload,
    })
    setSigned(true)
    setNewToken(newToken)

    if (isBorrower) {
      setLoading(true)
      const { data, conditions, signedStatus, assetStatus, borrowerRequiredParties, documents, pipelineAttention } =
        await getBorrowerOutstandingItems()
      setPipelineAttention(pipelineAttention || {})
      const filteredLoan = data.filter((item: any) => {
        const loanNumber = item.no
        let conditionCleared = true
        conditions[loanNumber]?.map((condition: any) => {
          if (!condition.cleared) conditionCleared = false
        })
        if (borrowerRequiredParties[loanNumber]) {
          Object.keys(borrowerRequiredParties[loanNumber]).map((key) => {
            if (!isPartyFilled(borrowerRequiredParties[loanNumber][key])) conditionCleared = false
          })
        }
        return !(conditionCleared && signedStatus[loanNumber] && assetStatus[loanNumber])
      })
      setLoading(false)
      if (!filteredLoan.length) {
        goToPageAfterSigned(newToken)
        return
      }
      setLoans(filteredLoan)
      setConditions(conditions)
      setDocuments(documents)
      setSignedStatus(signedStatus)
      setAssetStatus(assetStatus)
      setBorrowerRequiredParties(borrowerRequiredParties)
    } else {
      goToPageAfterSigned(newToken)
    }
  }

  const goToPageAfterSigned = (_token?: string) => {
    onCloseAttention()
    const { pathname } = location
    let newUrl
    if (pathname.includes('borrowerlogin')) newUrl = '/borrowerPipelines'
    else if (
      pathname.startsWith('/borrowerPipeline') ||
      pathname.startsWith('/investorPipeline') ||
      pathname.startsWith('/participantPipeline') ||
      pathname.startsWith('/participantPortal') ||
      // `/investorPortal` belongs here with the rest of the portals: the else-branch below
      // substitutes the fresh JWT into the path, which on `/investorPortal/signin` produced
      // `/investorPortal/<jwt>` - the token in the address bar and in history, and a URL long
      // enough that `isTemporaryUrl` swapped the portal nav out for the plain header.
      pathname.startsWith('/investorPortal')
    )
      newUrl = `${pathname.replace(`/${token}`, '')}${location.search}`
    else newUrl = `${pathname.replace(token, _token || _newToken)}${location.search}`

    navigate.push(newUrl)
  }

  const onCloseAttention = () => {
    dispatch({
      type: USER_ATTETION_VIEWED,
    })
    if (pathname.includes('borrowerlogin')) navigate.push('/borrowerPipelines')
  }

  if (!isLoaded) return <LayoutLoading show />

  if (!noAuth && requireSign && !isSigned) {
    if (isParticipant)
      return (
        <div className="Temporary-Layout-container">
          <HeaderSimple />
          <ParticipantAuth token={'signin'} onSigned={onSigned} />
          <Footer />
        </div>
      )
    else if (pathname.includes('/investorPipeline'))
      return (
        <div className="Temporary-Layout-container">
          <HeaderSimple />
          <InvestorAuth token={'signin'} onSigned={onSigned} />
          <Footer />
        </div>
      )
    else
      return (
        <div className="Temporary-Layout-container">
          <HeaderSimple />
          <TemporaryAuth token={'signin'} onSigned={onSigned} />
          <Footer />
        </div>
      )
  }

  if (!attentionShowed && loans.length && isBorrower && !isLoanOptionsReview) {
    return (
      <div className="Temporary-Layout-container">
        {impersonationBanner}
        <HeaderSimple />
        <LayoutLoading show={isLoading} />
        <div className={'home-container sm:text-center lg:text-left w-full block mt-0 relative bg-stone-100 md:py-20'}>
          <div className={'max-w-screen-2xl m-auto lg:pt-10 md:pt-5 sm:pt-2 pb-10 px-2 pt-4'}>
            {!!loans.length && (
              <BorrowerAttentionModal
                loans={loans}
                conditions={conditions}
                documents={documents}
                signedStatus={signedStatus}
                assetStatus={assetStatus}
                borrowerRequiredParties={borrowerRequiredParties}
                pipelineAttention={pipelineAttention}
                onClose={onCloseAttention}
              />
            )}
          </div>
        </div>
        <Footer />
      </div>
    )
  }
  return (
    // Portal pages upload by drag and drop the same way the internal ones do, and `useDrop` throws
    // without this context - the internal screens get it from BaseLayout.
    <DndProvider backend={HTML5Backend}>
      <div className="Temporary-Layout-container">
        {impersonationBanner}
        {noHeader ? null : isTemporaryUrl ? (
          <HeaderSimple />
        ) : isSigned && requireSign ? (
          isBorrower ? (
            <BorrowerHeader />
          ) : isParticipant ? (
            <ParticipantHeader />
          ) : (
            <InvestorHeader />
          )
        ) : (
          loanType === LoanType.DEFAULT && <HeaderSimple />
        )}

        <main className="relative mx-auto main">
          <LayoutLoading show={isLoading} />
          {isLoaded ? (
            noOverview ? (
              <div>{children}</div>
            ) : (
              <div className="px-2 mt-6">
                <Overview
                  title={`${props.title}${borrowerSep ? ` (${BorrowerLabel()[borrowerSep]})` : ''}`}
                  hasBackButton={hasBackButton}
                  backLink={props.backLink || `/borrowerPipeline/overview/${loanNumber}`}
                  noCompanyTitle={props.title == 'Asset Verification'}
                />
                {noShadow ? (
                  children
                ) : (
                  <div className="max-w-screen-2xl m-auto mb-4">
                    <div className="relative bg-white shadow1 rounded p-7 min-h-[100px]">{children}</div>
                  </div>
                )}
              </div>
            )
          ) : null}
        </main>

        {noHeader
          ? null
          : (loanNumber === undefined || loanType === LoanType.DEFAULT) && (
              <div className="mt-6">
                <Footer />
              </div>
            )}
      </div>
    </DndProvider>
  )
}
